// Shared navbar: floating glass pill that condenses and hides on scroll, spring-driven active indicator,
// "The yard" mega menu, ⌘K command palette, morphing theme toggle, full-screen mobile menu.
import { onFrame, readRamps, root, state } from "./core.ts"

export function initNav(): void {
  const nav = document.getElementById("nav")
  if (!nav) return
  const links = nav.querySelector<HTMLElement>(".links")
  const ind = nav.querySelector<HTMLElement>(".ind")
  if (!links || !ind) return
  const items = [...links.querySelectorAll<HTMLElement>(":scope > li > a, :scope > li > button")]

  /* condense + hide on scroll down, reveal on scroll up */
  let lastY = scrollY
  let acc = 0
  const anyOpen = () =>
    nav.querySelector(".mega.open") ||
    document.querySelector(".mnav.open") ||
    document.querySelector(".cmdk.open")
  addEventListener(
    "scroll",
    () => {
      const y = scrollY
      const d = y - lastY
      lastY = y
      nav.classList.toggle("condensed", y > 40)
      acc = Math.sign(d) === Math.sign(acc) ? acc + d : d
      if (y < 120 || acc < -6 || anyOpen()) nav.classList.remove("hide")
      else if (acc > 12) nav.classList.add("hide")
    },
    { passive: true },
  )

  /* spring indicator (k 320, c 30: ~0.35s settle with a hint of overshoot) */
  const S = { x: 0, w: 0, vx: 0, vw: 0, tx: 0, tw: 0, o: 0, init: false }
  const current = items.find((a) => a.getAttribute("aria-current") === "page")
  const target = (el: HTMLElement | undefined) => {
    if (!el) {
      S.o = 0
      return
    }
    const lr = links.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    S.tx = r.left - lr.left
    S.tw = r.width
    S.o = 1
    if (!S.init || state.reduced) {
      S.x = S.tx
      S.w = S.tw
      S.init = true
    }
  }
  for (const a of items) {
    a.addEventListener("pointerenter", () => target(a))
    a.addEventListener("focus", () => target(a))
  }
  links.addEventListener("pointerleave", () => target(current))
  new ResizeObserver(() => {
    S.init = false
    target(current)
  }).observe(links)
  target(current)
  onFrame((dt) => {
    const k = 320
    const c = 30
    const h = Math.min(dt, 1 / 30)
    if (state.reduced) {
      S.x = S.tx
      S.w = S.tw
    } else {
      S.vx += (k * (S.tx - S.x) - c * S.vx) * h
      S.x += S.vx * h
      S.vw += (k * (S.tw - S.w) - c * S.vw) * h
      S.w += S.vw * h
    }
    ind.style.setProperty("--x", `${S.x.toFixed(2)}px`)
    ind.style.setProperty("--w", `${Math.max(0, S.w).toFixed(2)}px`)
    ind.style.setProperty("--o", String(S.o))
  })

  initMega(nav)
  initTheme(nav)
  initMobile(nav)
  initPalette()
}

/* ---------- mega menu ---------- */
function initMega(nav: HTMLElement): void {
  const yb = nav.querySelector<HTMLElement>("#yardBtn")
  const mega = nav.querySelector<HTMLElement>("#mega")
  const li = yb?.parentElement
  if (!yb || !mega || !li) return
  let tOpen: ReturnType<typeof setTimeout> | undefined
  let tClose: ReturnType<typeof setTimeout> | undefined
  const setMega = (open: boolean, focus?: boolean) => {
    clearTimeout(tOpen)
    clearTimeout(tClose)
    mega.classList.toggle("open", open)
    yb.setAttribute("aria-expanded", String(open))
    if (open && focus) mega.querySelector<HTMLElement>("a")?.focus()
  }
  // A click with detail 0 came from the keyboard, so the first link takes focus.
  yb.addEventListener("click", (e) => setMega(yb.getAttribute("aria-expanded") !== "true", e.detail === 0))
  li.addEventListener("pointerenter", (e) => {
    if (e.pointerType === "mouse") tOpen = setTimeout(() => setMega(true), 90)
  })
  li.addEventListener("pointerleave", () => {
    clearTimeout(tOpen)
    tClose = setTimeout(() => setMega(false), 180)
  })
  mega.addEventListener("pointerenter", () => clearTimeout(tClose))
  li.addEventListener("focusout", (e) => {
    if (!li.contains(e.relatedTarget instanceof Node ? e.relatedTarget : null)) setMega(false)
  })
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && mega.classList.contains("open")) {
      setMega(false)
      yb.focus()
    }
  })
  document.addEventListener("pointerdown", (e) => {
    if (!li.contains(e.target instanceof Node ? e.target : null)) setMega(false)
  })
}

/* ---------- theme: light/dark switch with morphing icon ---------- */
function initTheme(_nav: HTMLElement): void {
  const eff = () =>
    root.dataset.theme || (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
  const syncMode = () => {
    root.dataset.mode = eff()
    for (const b of document.querySelectorAll(".theme-btn")) {
      b.setAttribute("aria-label", `Switch to ${eff() === "dark" ? "light" : "dark"} theme`)
    }
  }
  const toggleTheme = () => {
    const next = eff() === "dark" ? "light" : "dark"
    root.dataset.theme = next
    localStorage.setItem("fy-theme", next)
    syncMode()
    readRamps()
    document
      .querySelector('meta[name="theme-color"]:not([media])')
      ?.setAttribute("content", getComputedStyle(root).getPropertyValue("--background-100").trim())
  }
  for (const b of document.querySelectorAll(".theme-btn")) b.addEventListener("click", toggleTheme)
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", syncMode)
  syncMode()
}

/* ---------- mobile overlay ---------- */
function initMobile(nav: HTMLElement): void {
  const mb = nav.querySelector<HTMLElement>(".menu-btn")
  const mn = document.getElementById("mnav")
  if (!mb || !mn) return
  const setMobile = (open: boolean) => {
    mn.classList.toggle("open", open)
    mb.setAttribute("aria-expanded", String(open))
    const label = mb.querySelector(".sr")
    if (label) label.textContent = open ? "Close menu" : "Open menu"
    document.body.style.overflow = open ? "hidden" : ""
    mn.inert = !open
    if (open) setTimeout(() => mn.querySelector<HTMLElement>("a")?.focus(), 60)
  }
  mn.inert = true
  mb.addEventListener("click", () => setMobile(mb.getAttribute("aria-expanded") !== "true"))
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && mn.classList.contains("open")) {
      setMobile(false)
      mb.focus()
    }
  })
  mn.addEventListener("click", (e) => {
    if (e.target instanceof Element && e.target.closest("a")) setMobile(false)
  })
}

/* ---------- ⌘K command palette over apps and pages ---------- */
interface SearchApp {
  readonly name: string
  readonly job: string
  readonly family: string
  readonly familyName: string
  readonly url: string
}
interface SearchPage {
  readonly title: string
  readonly url: string
}
interface SearchIndex {
  readonly apps: ReadonlyArray<SearchApp>
  readonly pages: ReadonlyArray<SearchPage>
}

/** Prefix beats substring beats in-order subsequence; 0 means no match. */
const score = (q: string, text: string): number => {
  const s = text.toLowerCase()
  if (!q) return 1
  if (s.startsWith(q)) return 3
  if (s.includes(q)) return 2
  let i = 0
  for (const ch of s) if (ch === q[i]) i++
  return i === q.length ? 1 : 0
}

function initPalette(): void {
  const box = document.getElementById("cmdk")
  const input = box?.querySelector("input")
  const list = box?.querySelector<HTMLElement>(".cmdk-list")
  if (!box || !input || !list) return
  let index: SearchIndex | null = null
  let sel = 0
  let results: ReadonlyArray<SearchApp | SearchPage> = []
  let lastFocus: Element | null = null
  const load = async (): Promise<SearchIndex> => {
    if (index) return index
    index = await fetch("/assets/data/search.json")
      .then((r) => r.json() as Promise<SearchIndex>)
      .catch((): SearchIndex => ({ apps: [], pages: [] }))
    return index
  }
  const render = (data: SearchIndex) => {
    const q = input.value.trim().toLowerCase()
    const apps = data.apps
      .map((a) => ({ ...a, s: Math.max(score(q, a.name) * 2, score(q, a.job), score(q, a.familyName)) }))
      .filter((a) => a.s)
      .sort((a, b) => b.s - a.s)
    const pages = data.pages
      .map((p) => ({ ...p, s: score(q, p.title) }))
      .filter((p) => p.s)
      .sort((a, b) => b.s - a.s)
    results = [...apps.slice(0, 8), ...pages.slice(0, 6)]
    sel = Math.min(sel, Math.max(0, results.length - 1))
    if (!results.length) {
      list.innerHTML = `<li class="cmdk-empty">Nothing matches “${input.value.replace(/[<&]/g, "")}”.</li>`
      return
    }
    let html = ""
    let i = 0
    if (apps.length) {
      html += `<li class="grp" role="presentation">Apps</li>${apps
        .slice(0, 8)
        .map(
          (a) =>
            `<li role="none"><a role="option" id="ck${i}" href="${a.url}" aria-selected="${i === sel}" data-i="${i++}"><span class="sw ${a.family}"></span>${a.name}<span class="k">${a.familyName}</span></a></li>`,
        )
        .join("")}`
    }
    if (pages.length) {
      html += `<li class="grp" role="presentation">Pages</li>${pages
        .slice(0, 6)
        .map(
          (p) =>
            `<li role="none"><a role="option" id="ck${i}" href="${p.url}" aria-selected="${i === sel}" data-i="${i++}">${p.title}<span class="k">Page</span></a></li>`,
        )
        .join("")}`
    }
    list.innerHTML = html
    input.setAttribute("aria-activedescendant", `ck${sel}`)
  }
  const rerender = () => {
    if (index) render(index)
  }
  const open = async () => {
    lastFocus = document.activeElement
    const data = await load()
    box.classList.add("open")
    input.value = ""
    sel = 0
    render(data)
    input.focus()
    document.body.style.overflow = "hidden"
  }
  const close = () => {
    box.classList.remove("open")
    document.body.style.overflow = ""
    if (lastFocus instanceof HTMLElement || lastFocus instanceof SVGElement) lastFocus.focus()
  }
  for (const b of document.querySelectorAll("[data-cmdk]")) b.addEventListener("click", () => void open())
  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault()
      if (box.classList.contains("open")) close()
      else void open()
      return
    }
    if (!box.classList.contains("open")) return
    if (e.key === "Escape") {
      e.preventDefault()
      close()
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      sel = (sel + (e.key === "ArrowDown" ? 1 : -1) + results.length) % Math.max(1, results.length)
      rerender()
      const a = list.querySelector<HTMLElement>('[aria-selected="true"]')
      if (a) {
        const t = a.offsetTop
        const b = t + a.offsetHeight
        if (t < list.scrollTop) list.scrollTop = t - 8
        else if (b > list.scrollTop + list.clientHeight) list.scrollTop = b - list.clientHeight + 8
      }
    } else if (e.key === "Enter") {
      const a = list.querySelector('[aria-selected="true"]')
      const href = a?.getAttribute("href")
      if (href) {
        e.preventDefault()
        location.href = href
      }
    } else if (e.key === "Tab") {
      e.preventDefault()
      input.focus()
    }
  })
  input.addEventListener("input", () => {
    sel = 0
    rerender()
  })
  box.addEventListener("pointerdown", (e) => {
    if (e.target === box) close()
  })
  list.addEventListener("pointermove", (e) => {
    const a = e.target instanceof Element ? e.target.closest<HTMLElement>("[data-i]") : null
    if (a && Number(a.dataset.i) !== sel) {
      sel = Number(a.dataset.i)
      rerender()
    }
  })
}
